import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuthStore } from '../store/adminAuthStore';
import ConfirmDialog from '../components/ConfirmDialog';
import { getApiUrl, getApiKey } from '../utils/apiConfig';

interface AdminUser {
  userId: string;
  email: string;
  name?: string;
  familyId?: string;
  createdAt?: string;
  updatedAt?: string;
  emailVerified?: boolean;
  status?: string;
}

interface AdminFamily {
  familyId: string;
  name: string;
  ownerId: string;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

interface FamilyMember {
  userId?: string;
  email?: string;
  name: string;
  role: string;
}

interface FamilyDetails {
  family: AdminFamily;
  members: FamilyMember[];
}

interface UserFamily {
  familyId: string;
  name: string;
  ownerId: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

interface AdminInvitation {
  email: string;
  familyId: string;
  invitationId: string;
  familyName: string;
  invitedBy: string;
  invitedByName: string;
  role: 'parent' | 'adult';
  name: string;
  color: string;
  status: 'pending' | 'accepted' | 'cancelled';
  createdAt: string;
  expiresAt: number;
  acceptedAt?: string;
}

interface PaginatedResponse<T> {
  items: T[];
  count: number;
  lastKey?: string;
  hasMore: boolean;
}

interface EmailSummary {
  key: string;
  receivedDate: string;
  size: number;
  from?: string;
  to?: string;
  subject?: string;
}

interface ParsedEmail {
  key: string;
  from: string;
  to: string;
  subject: string;
  date: string;
  body: string;
  htmlBody?: string;
  textBody?: string;
  headers: Record<string, string>;
}

export default function Admin() {
  const navigate = useNavigate();
  const { getIdToken, isAuthenticated, initializeTokenRefresh } = useAdminAuthStore();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [families, setFamilies] = useState<AdminFamily[]>([]);
  const [invitations, setInvitations] = useState<AdminInvitation[]>([]);
  const [emails, setEmails] = useState<EmailSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'families' | 'invitations' | 'emails'>('users');
  const [selectedEmail, setSelectedEmail] = useState<ParsedEmail | null>(null);
  const [showEmailDetails, setShowEmailDetails] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState<FamilyDetails | null>(null);
  const [showFamilyDetails, setShowFamilyDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [familyToDelete, setFamilyToDelete] = useState<AdminFamily | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ user: AdminUser; families: string[] } | null>(null);
  const [userFamilies, setUserFamilies] = useState<UserFamily[]>([]);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [addingToFamily, setAddingToFamily] = useState(false);
  const [removingFromFamily, setRemovingFromFamily] = useState<string | null>(null);
  const [selectedFamilyToAdd, setSelectedFamilyToAdd] = useState('');
  const [newFamilyRole, setNewFamilyRole] = useState<'owner' | 'parent' | 'child' | 'adult'>('adult');

  useEffect(() => {
    initializeTokenRefresh();
  }, [initializeTokenRefresh]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login', { replace: true });
      return;
    }
    loadData();
  }, [isAuthenticated, navigate, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiKey = getApiKey();
      const apiUrl = getApiUrl();

      if (activeTab === 'users') {
        const response = await fetch(`${apiUrl}/admin/users`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Api-Key': apiKey || '',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error('Unauthorized: You must be signed in with an admin account (admin user pool) to access this page. Please sign out and sign in with your admin credentials.');
          }
          throw new Error(`Failed to load users: ${response.statusText}`);
        }

        const data: PaginatedResponse<AdminUser> = await response.json();
        setUsers(data.items || []);
      } else if (activeTab === 'families') {
        const response = await fetch(`${apiUrl}/admin/families`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Api-Key': apiKey || '',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error('Unauthorized: You must be signed in with an admin account (admin user pool) to access this page. Please sign out and sign in with your admin credentials.');
          }
          throw new Error(`Failed to load families: ${response.statusText}`);
        }

        const data: PaginatedResponse<AdminFamily> = await response.json();
        setFamilies(data.items || []);
      } else if (activeTab === 'invitations') {
        const response = await fetch(`${apiUrl}/admin/invitations?status=pending`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Api-Key': apiKey || '',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error('Unauthorized: You must be signed in with an admin account (admin user pool) to access this page. Please sign out and sign in with your admin credentials.');
          }
          throw new Error(`Failed to load invitations: ${response.statusText}`);
        }

        const data = await response.json();
        setInvitations(data.invitations || []);
      } else if (activeTab === 'emails') {
        const response = await fetch(`${apiUrl}/admin/emails?limit=100`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Api-Key': apiKey || '',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error('Unauthorized: You must be signed in with an admin account (admin user pool) to access this page. Please sign out and sign in with your admin credentials.');
          }
          throw new Error(`Failed to load emails: ${response.statusText}`);
        }

        const data = await response.json();
        setEmails(data.emails || []);
      }
    } catch (err: any) {
      console.error('Error loading admin data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadFamilyDetails = async (familyId: string) => {
    try {
      const token = getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiKey = getApiKey();
      const apiUrl = getApiUrl();

      const response = await fetch(`${apiUrl}/admin/families/${familyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': apiKey || '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load family details: ${response.statusText}`);
      }

      const data = await response.json();
      setSelectedFamily(data);
      setShowFamilyDetails(true);
    } catch (err: any) {
      console.error('Error loading family details:', err);
      setError(err.message || 'Failed to load family details');
    }
  };

  const loadUserDetails = async (userId: string) => {
    try {
      const token = getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiKey = getApiKey();
      const apiUrl = getApiUrl();

      // Load user details
      const userResponse = await fetch(`${apiUrl}/admin/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': apiKey || '',
          'Content-Type': 'application/json',
        },
      });

      if (!userResponse.ok) {
        throw new Error(`Failed to load user details: ${userResponse.statusText}`);
      }

      const userData = await userResponse.json();

      // Load user's families with details
      const familiesResponse = await fetch(`${apiUrl}/admin/users/${userId}/families`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': apiKey || '',
          'Content-Type': 'application/json',
        },
      });

      if (!familiesResponse.ok) {
        throw new Error(`Failed to load user families: ${familiesResponse.statusText}`);
      }

      const familiesData = await familiesResponse.json();
      setUserFamilies(familiesData.families || []);
      setSelectedUser(userData);
      setShowUserDetails(true);
    } catch (err: any) {
      console.error('Error loading user details:', err);
      setError(err.message || 'Failed to load user details');
    }
  };

  const loadEmailDetails = async (key: string) => {
    setLoadingEmail(true);
    try {
      const token = getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiKey = getApiKey();
      const apiUrl = getApiUrl();

      // Encode the key for URL
      const encodedKey = encodeURIComponent(key);

      const response = await fetch(`${apiUrl}/admin/emails/${encodedKey}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': apiKey || '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load email: ${response.statusText}`);
      }

      const data = await response.json();
      setSelectedEmail(data);
      setShowEmailDetails(true);
    } catch (err: any) {
      console.error('Error loading email details:', err);
      setError(err.message || 'Failed to load email details');
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleAddUserToFamily = async () => {
    if (!selectedUser || !selectedFamilyToAdd) return;

    setAddingToFamily(true);
    try {
      const token = getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiKey = getApiKey();
      const apiUrl = getApiUrl();

      const response = await fetch(`${apiUrl}/admin/users/${selectedUser.user.userId}/families/${selectedFamilyToAdd}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': apiKey || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          role: newFamilyRole,
          name: selectedUser.user.name || selectedUser.user.email.split('@')[0],
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add user to family');
      }

      // Reload user families
      await loadUserDetails(selectedUser.user.userId);
      setSelectedFamilyToAdd('');
      setNewFamilyRole('adult');
    } catch (err: any) {
      console.error('Error adding user to family:', err);
      setError(err.message || 'Failed to add user to family');
    } finally {
      setAddingToFamily(false);
    }
  };

  const handleRemoveUserFromFamily = async (familyId: string) => {
    if (!selectedUser) return;

    setRemovingFromFamily(familyId);
    try {
      const token = getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiKey = getApiKey();
      const apiUrl = getApiUrl();

      const response = await fetch(`${apiUrl}/admin/users/${selectedUser.user.userId}/families/${familyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': apiKey || '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to remove user from family');
      }

      // Reload user families
      await loadUserDetails(selectedUser.user.userId);
    } catch (err: any) {
      console.error('Error removing user from family:', err);
      setError(err.message || 'Failed to remove user from family');
    } finally {
      setRemovingFromFamily(null);
    }
  };

  const handleDeleteFamily = async () => {
    if (!familyToDelete) return;

    setDeleting(true);
    try {
      const token = getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiKey = getApiKey();
      const apiUrl = getApiUrl();

      const response = await fetch(`${apiUrl}/admin/families/${familyToDelete.familyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': apiKey || '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete family');
      }

      // Remove from local state
      setFamilies(families.filter(f => f.familyId !== familyToDelete.familyId));
      setShowDeleteConfirm(false);
      setFamilyToDelete(null);
    } catch (err: any) {
      console.error('Error deleting family:', err);
      setError(err.message || 'Failed to delete family');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeletingUser(true);
    try {
      const token = getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiKey = getApiKey();
      const apiUrl = getApiUrl();

      const response = await fetch(`${apiUrl}/admin/users/${userToDelete.userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': apiKey || '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete user');
      }

      // Remove from local state
      setUsers(users.filter(u => u.userId !== userToDelete.userId));
      setShowDeleteUserConfirm(false);
      setUserToDelete(null);
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-sm-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading admin data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-600 dark:text-red-400 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-700 dark:text-gray-300 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
          >
            Go to Calendar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Admin Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage users and families
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('families')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'families'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Families ({families.length})
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invitations'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Pending Invitations ({invitations.length})
            </button>
            <button
              onClick={() => setActiveTab('emails')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'emails'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Emails ({emails.length})
            </button>
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'users' ? (
          <div className="bg-white dark:bg-gray-800 rounded-sm shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      User ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Family ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <tr key={user.userId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {user.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                        {user.userId.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-sm-full ${
                          user.status === 'CONFIRMED'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {user.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                        {user.familyId ? user.familyId.substring(0, 8) + '...' : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => loadUserDetails(user.userId)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 min-h-[44px] min-w-[44px] px-3 py-2 rounded-sm"
                            title="View Details"
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              setUserToDelete(user);
                              setShowDeleteUserConfirm(true);
                            }}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 min-h-[44px] min-w-[44px] px-3 py-2 rounded-landing"
                            title="Delete User"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No users found
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'families' ? (
          <div className="bg-white dark:bg-gray-800 rounded-sm shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Family Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Family ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Owner ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Members
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {families.map((family) => (
                    <tr key={family.familyId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        {family.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                        {family.familyId.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                        {family.ownerId.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {family.memberCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(family.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => loadFamilyDetails(family.familyId)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 min-h-[44px] min-w-[44px] px-3 py-2 rounded-sm"
                            title="View Details"
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              setFamilyToDelete(family);
                              setShowDeleteConfirm(true);
                            }}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 min-h-[44px] min-w-[44px] px-3 py-2 rounded-landing"
                            title="Delete Family"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {families.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No families found
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-sm shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Family
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Invited By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Expires
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {invitations.map((invitation) => {
                    const expiresDate = new Date(invitation.expiresAt * 1000);
                    const isExpired = expiresDate < new Date();
                    return (
                      <tr key={invitation.invitationId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {invitation.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {invitation.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {invitation.familyName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-sm-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {invitation.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {invitation.invitedByName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(invitation.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={isExpired ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}>
                            {expiresDate.toLocaleDateString()}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {invitations.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No pending invitations
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'emails' && (
          <div className="bg-white dark:bg-gray-800 rounded-sm shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      From
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Received
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {emails.map((email) => {
                    const receivedDate = new Date(email.receivedDate);
                    const sizeKB = (email.size / 1024).toFixed(2);
                    return (
                      <tr key={email.key} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {email.from || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {email.to || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate">
                          {email.subject || '(No Subject)'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {receivedDate.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {sizeKB} KB
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => loadEmailDetails(email.key)}
                            disabled={loadingEmail}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                          >
                            {loadingEmail ? 'Loading...' : 'View'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {emails.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No emails received yet
                </div>
              )}
            </div>
          </div>
        )}

        {/* Family Details Modal */}
        {showFamilyDetails && selectedFamily && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowFamilyDetails(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-sm shadow-xl max-w-2xl w-full p-6 z-10 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Family: {selectedFamily.family.name}
                </h2>
                <button
                  onClick={() => setShowFamilyDetails(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 min-h-[44px] min-w-[44px]"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Family ID</h3>
                  <p className="text-sm font-mono text-gray-900 dark:text-gray-100">{selectedFamily.family.familyId}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Owner ID</h3>
                  <p className="text-sm font-mono text-gray-900 dark:text-gray-100">{selectedFamily.family.ownerId}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Member Count</h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{selectedFamily.family.memberCount}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Members</h3>
                  {selectedFamily.members.length > 0 ? (
                    <div className="space-y-2">
                      {selectedFamily.members.map((member, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-sm">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">{member.name}</p>
                              {member.email && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">{member.email}</p>
                              )}
                              {member.userId && (
                                <p className="text-xs font-mono text-gray-500 dark:text-gray-500 mt-1">
                                  {member.userId.substring(0, 8)}...
                                </p>
                              )}
                            </div>
                            <span className="px-2 py-1 text-xs font-semibold rounded-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {member.role}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No members</p>
                  )}
                </div>
                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => {
                      setFamilyToDelete(selectedFamily.family);
                      setShowFamilyDetails(false);
                      setShowDeleteConfirm(true);
                    }}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-sm transition-colors min-h-[48px]"
                  >
                    Delete Family
                  </button>
                  <button
                    onClick={() => setShowFamilyDetails(false)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold py-3 px-6 rounded-sm transition-colors min-h-[48px]"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Details Modal */}
        {showUserDetails && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowUserDetails(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-sm shadow-xl max-w-2xl w-full p-6 z-10 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  User: {selectedUser.user.name || selectedUser.user.email}
                </h2>
                <button
                  onClick={() => setShowUserDetails(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 min-h-[44px] min-w-[44px]"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Email</h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{selectedUser.user.email}</p>
                </div>
                {selectedUser.user.name && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Name</h3>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{selectedUser.user.name}</p>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">User ID</h3>
                  <p className="text-sm font-mono text-gray-900 dark:text-gray-100">{selectedUser.user.userId}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Status</h3>
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-sm-full ${
                    selectedUser.user.status === 'CONFIRMED'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}>
                    {selectedUser.user.status || 'Unknown'}
                  </span>
                </div>
                {selectedUser.user.emailVerified !== undefined && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Email Verified</h3>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-sm-full ${
                      selectedUser.user.emailVerified
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {selectedUser.user.emailVerified ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
                {selectedUser.user.familyId && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Primary Family ID</h3>
                    <p className="text-sm font-mono text-gray-900 dark:text-gray-100">{selectedUser.user.familyId}</p>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Families ({userFamilies.length})
                  </h3>
                  {userFamilies.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      {userFamilies.map((userFamily) => (
                        <div key={userFamily.familyId} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-sm flex justify-between items-center">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{userFamily.name}</p>
                            <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1">
                              {userFamily.familyId.substring(0, 8)}...
                            </p>
                            <span className="mt-1 inline-block px-2 py-1 text-xs font-semibold rounded-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {userFamily.role}
                            </span>
                          </div>
                          {userFamily.role !== 'owner' && (
                            <button
                              onClick={() => handleRemoveUserFromFamily(userFamily.familyId)}
                              disabled={removingFromFamily === userFamily.familyId}
                              className="ml-3 text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 min-h-[44px] min-w-[44px] px-3 py-2 rounded-sm disabled:opacity-50"
                              title="Remove from Family"
                            >
                              {removingFromFamily === userFamily.familyId ? 'Removing...' : 'Remove'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">No families</p>
                  )}
                  
                  {/* Add to Family Form */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Add to Family</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Select Family
                        </label>
                        <select
                          value={selectedFamilyToAdd}
                          onChange={(e) => setSelectedFamilyToAdd(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[48px]"
                        >
                          <option value="">Choose a family...</option>
                          {families
                            .filter(f => !userFamilies.some(uf => uf.familyId === f.familyId))
                            .map((family) => (
                              <option key={family.familyId} value={family.familyId}>
                                {family.name} ({family.familyId.substring(0, 8)}...)
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Role
                        </label>
                        <select
                          value={newFamilyRole}
                          onChange={(e) => setNewFamilyRole(e.target.value as 'owner' | 'parent' | 'child' | 'adult')}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[48px]"
                        >
                          <option value="adult">Adult</option>
                          <option value="parent">Parent</option>
                          <option value="child">Child</option>
                          <option value="owner">Owner</option>
                        </select>
                      </div>
                      <button
                        onClick={handleAddUserToFamily}
                        disabled={!selectedFamilyToAdd || addingToFamily}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-sm transition-colors min-h-[48px]"
                      >
                        {addingToFamily ? 'Adding...' : 'Add to Family'}
                      </button>
                    </div>
                  </div>
                </div>
                {selectedUser.user.createdAt && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Created</h3>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {new Date(selectedUser.user.createdAt).toLocaleString()}
                    </p>
                  </div>
                )}
                {selectedUser.user.updatedAt && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Last Updated</h3>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {new Date(selectedUser.user.updatedAt).toLocaleString()}
                    </p>
                  </div>
                )}
                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => {
                      setUserToDelete(selectedUser.user);
                      setShowUserDetails(false);
                      setShowDeleteUserConfirm(true);
                    }}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-sm transition-colors min-h-[48px]"
                  >
                    Delete User
                  </button>
                  <button
                    onClick={() => setShowUserDetails(false)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold py-3 px-6 rounded-sm transition-colors min-h-[48px]"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Details Modal */}
        {showEmailDetails && selectedEmail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowEmailDetails(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-sm shadow-xl max-w-4xl w-full p-6 z-10 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Email Details
                </h2>
                <button
                  onClick={() => setShowEmailDetails(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 min-h-[44px] min-w-[44px]"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">From</h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100 break-all">{selectedEmail.from}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">To</h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100 break-all">{selectedEmail.to}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Subject</h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{selectedEmail.subject}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Date</h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{new Date(selectedEmail.date).toLocaleString()}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Body</h3>
                  {selectedEmail.htmlBody ? (
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-sm border border-gray-200 dark:border-gray-600">
                      <div 
                        className="prose prose-sm dark:prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: selectedEmail.htmlBody }}
                      />
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-sm border border-gray-200 dark:border-gray-600">
                      <pre className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap font-sans">
                        {selectedEmail.textBody || selectedEmail.body}
                      </pre>
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Headers</h3>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-sm border border-gray-200 dark:border-gray-600 max-h-60 overflow-y-auto">
                    <div className="space-y-1">
                      {Object.entries(selectedEmail.headers).map(([key, value]) => (
                        <div key={key} className="text-xs">
                          <span className="font-semibold text-gray-700 dark:text-gray-300">{key}:</span>{' '}
                          <span className="text-gray-600 dark:text-gray-400 break-all">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => setShowEmailDetails(false)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold py-3 px-6 rounded-sm transition-colors min-h-[48px]"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Family Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Delete Family"
          message={`Are you sure you want to delete the family "${familyToDelete?.name}"? This will remove all members from the family and delete all associated events. This action cannot be undone.`}
          confirmText={deleting ? "Deleting..." : "Delete"}
          cancelText="Cancel"
          onConfirm={handleDeleteFamily}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setFamilyToDelete(null);
          }}
          isDestructive={true}
        />

        {/* Delete User Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteUserConfirm}
          title="Delete User"
          message={`Are you sure you want to delete the user "${userToDelete?.email}"? This will remove the user from all families, delete all their events, and remove their photos. This action cannot be undone.`}
          confirmText={deletingUser ? "Deleting..." : "Delete"}
          cancelText="Cancel"
          onConfirm={handleDeleteUser}
          onCancel={() => {
            setShowDeleteUserConfirm(false);
            setUserToDelete(null);
          }}
          isDestructive={true}
        />
      </div>
    </div>
  );
}
